import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { AlertTriangle, Loader2, UserCheck, Users } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { CompositionPicker, sectorLabel } from "./CompositionPicker";
import { JudgeOptionLabel } from "./JudgeOptionLabel";

const normName = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim().toLowerCase();
const asNames = (ms) => (ms || []).filter(m => !(m && typeof m === "object" && m.excluded)).map(m => (typeof m === "string" ? m : m?.name)).filter(Boolean);

// Διάλογος ορισμού δικαστή προδικαστικού σε 1..N υποθέσεις (σελίδα «Προσδιορισμός»)
// target: { caseIds: [], courtType, composition, pinakioLabel, hearingDate, title, subtitle }
// Πολυμελές: Βήμα 1 «Ανάθεση σε Σύνθεση» (inline, αν λείπει) → Βήμα 2 ορισμός δικαστή-μέλους
export const AssignJudgeDialog = ({ target, onClose, onDone }) => {
  const [judges, setJudges] = useState([]);
  const [judgeId, setJudgeId] = useState("");
  const [busy, setBusy] = useState(false);
  const [comp, setComp] = useState(null);
  const ct = target?.courtType || "Μονομελές";
  const isPoly = ct === "Πολυμελές";
  const members = asNames(comp?.members);
  const memberSet = new Set(members.map(normName));
  const allowed = members.length ? judges.filter(j => memberSet.has(normName(j.full_name))) : judges;
  const shown = allowed.length ? allowed : judges;

  useEffect(() => {
    if (!target) return;
    setJudgeId("");
    setComp(target.composition || null);
    axios.get(`${API}/pretrial-judges-list?court_type=${encodeURIComponent(ct)}`)
      .then(r => setJudges(r.data?.judges || [])).catch(() => setJudges([]));
  }, [target, ct]);

  const submit = async () => {
    if (!judgeId) return;
    try {
      setBusy(true);
      const r = await axios.post(`${API}/assignment-board/assign`, { case_ids: target.caseIds, judge_id: judgeId, court_type: ct });
      toast.success(r.data?.message || "Ο δικαστής ορίστηκε");
      onDone?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα ορισμού δικαστή", { duration: 12000 });
    } finally { setBusy(false); }
  };

  const onCompositionDone = (c) => {
    setComp(c ? { ...c, members: asNames(c.members) } : null);
    setJudgeId("");
    onDone?.();
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className={isPoly ? "max-w-2xl" : "max-w-md"} data-testid="assign-judge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserCheck className="w-5 h-5 text-primary" />Ορισμός Δικαστή Προδικαστικού</DialogTitle>
          <DialogDescription>
            {target?.title}{target?.subtitle ? <><br /><span className="text-slate-500">{target.subtitle}</span></> : null}
            <br /><span className="text-xs">{ct} · {target?.caseIds?.length || 0} υπόθεση/εις</span>
          </DialogDescription>
        </DialogHeader>

        {isPoly && (
          <div className={`rounded border px-3 py-2 text-xs ${comp ? "border-violet-300 bg-violet-50 text-violet-950" : "border-amber-400 bg-amber-50 text-amber-900"}`}
               data-testid={comp ? "assign-judge-composition-hint" : "assign-judge-composition-required"}>
            {comp ? (
              <>
                <span className="flex items-center gap-1 font-semibold flex-wrap">
                  <Users className="w-3.5 h-3.5" />Βήμα 1 ✓ Σύνθεση «{comp.label}»{sectorLabel(comp) ? ` — ${sectorLabel(comp)}` : ""}
                </span>
                <span className="block text-slate-800"><span className="text-slate-600">Μέλη: </span>{members.join(", ")}</span>
                {!allowed.length && judges.length > 0 && (
                  <p className="text-red-700 mt-1">Κανένα μέλος της σύνθεσης δεν υπάρχει στη λίστα δικαστών {ct} — προσθέστε τα μέλη στους Δικαστές Προδικαστικού.</p>
                )}
              </>
            ) : (
              <span className="flex items-center gap-1 font-semibold">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-700" />Βήμα 1: Ανάθεση σε Σύνθεση — προηγείται του ορισμού δικαστή, ο οποίος πρέπει να είναι μέλος της.
              </span>
            )}
            {target?.pinakioLabel && target?.hearingDate && (
              <div className="mt-1.5">
                <CompositionPicker pinakio={{ pinakio_code: "dialog", label: target.pinakioLabel }}
                                   dateGroup={{ date: target.hearingDate, composition: comp }}
                                   defaultOpen={!comp} hideBadge onDone={onCompositionDone} />
              </div>
            )}
          </div>
        )}

        <div className="space-y-1">
          {isPoly && <p className="text-xs font-semibold text-slate-700" data-testid="assign-judge-step2-label">Βήμα 2: Δικαστής {comp ? "(μέλος της σύνθεσης)" : ""}</p>}
          <Select value={judgeId} onValueChange={setJudgeId}>
            <SelectTrigger data-testid="assign-judge-select">
              <SelectValue placeholder={judges.length ? "Επιλέξτε δικαστή…" : "Δεν υπάρχουν δικαστές για αυτόν τον τύπο"} />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              {shown.map(j => (
                <SelectItem key={j.id} value={j.id}>
                  <JudgeOptionLabel judge={j} />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="assign-judge-cancel">Άκυρο</Button>
          <Button onClick={submit} disabled={busy || !judgeId} data-testid="assign-judge-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ορισμός"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignJudgeDialog;
